import { useState, useEffect } from 'react';
import { FileText, Share2, Download, Copy, Check } from 'lucide-react';
import toast from 'react-hot-toast';
import { getPublicInsights, getPublicInsight } from '../../services/api';
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Cell, ReferenceLine
} from 'recharts';

interface PublicInsight {
  id: string;
  title: string;
  format: string;
  author_displayName: string;
  published_at: string;
  content_preview?: string;
  content?: string;
  charts?: any[];
}

function InsightChart({ chart }: { chart: any }) {
  if (chart.type === 'line' && chart.lines && chart.data?.length > 0) {
    return (
      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: '16px 12px', marginBottom: 16 }}>
        <h4 style={{ fontSize: '0.85rem', fontWeight: 600, color: '#374151', margin: '0 0 12px 4px' }}>{chart.title}</h4>
        <ResponsiveContainer width="100%" height={220}>
          <LineChart data={chart.data} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#9ca3af' }} />
            <YAxis tick={{ fontSize: 11, fill: '#9ca3af' }} domain={['auto', 'auto']} />
            <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
            {chart.lines.map((key: string, i: number) => (
              <Line key={key} type="monotone" dataKey={key} stroke={chart.colors?.[i] || '#3b82f6'}
                strokeWidth={i === 0 ? 2 : 1} dot={false} strokeDasharray={i > 0 ? '5 5' : undefined} />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>
    );
  }
  if (chart.type === 'bar' && chart.data?.length > 0) {
    return (
      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: '16px 12px', marginBottom: 16 }}>
        <h4 style={{ fontSize: '0.85rem', fontWeight: 600, color: '#374151', margin: '0 0 12px 4px' }}>{chart.title}</h4>
        <ResponsiveContainer width="100%" height={Math.max(200, chart.data.length * 28)}>
          <BarChart data={chart.data} layout="vertical" margin={{ top: 5, right: 30, left: 80, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" horizontal={false} />
            <XAxis type="number" tick={{ fontSize: 11, fill: '#9ca3af' }} />
            <YAxis type="category" dataKey={chart.nameKey || 'name'} tick={{ fontSize: 11, fill: '#374151' }} width={75} />
            <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
            <ReferenceLine x={0} stroke="#d1d5db" />
            <Bar dataKey={chart.dataKey || 'value'} radius={[0, 4, 4, 0]}>
              {chart.data.map((entry: any, idx: number) => (
                <Cell key={idx} fill={(entry[chart.dataKey || 'value'] || 0) >= 0 ? '#10b981' : '#ef4444'} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    );
  }
  return null;
}

export default function PublicInsightsView() {
  const [insights, setInsights] = useState<PublicInsight[]>([]);
  const [viewing, setViewing] = useState<PublicInsight | null>(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [visibleCount, setVisibleCount] = useState(12);

  const handleShare = async () => {
    if (!viewing) return;
    const url = `https://api.ailabquant.com/api/insights/share/${viewing.id}`;
    if (navigator.share) {
      try {
        await navigator.share({ title: viewing.title, text: viewing.title, url });
      } catch { /* cancelled */ }
    } else {
      await navigator.clipboard.writeText(url);
      toast.success('Link copied to clipboard');
    }
  };

  const handleCopy = () => {
    if (!viewing?.content) return;
    navigator.clipboard.writeText(viewing.content);
    setCopied(true);
    toast.success('Copied to clipboard');
    setTimeout(() => setCopied(false), 2000);
  };

  const handlePDF = () => {
    if (!viewing) return;
    const content = viewing.content || '';
    const isHtmlContent = content.includes('<p>') || content.includes('<h');
    const bodyHtml = isHtmlContent ? content : content
      .replace(/\\n\\n/g, '\n\n').replace(/\\n/g, '\n')
      .split(/\n\n+/).filter((p: string) => p.trim())
      .map((p: string) => `<p>${p.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')}</p>`)
      .join('\n');

    // Chart data as tables
    const charts = viewing.charts || [];
    const chartHtml = charts.length > 0
      ? '<h3 style="margin-top:30px;font-size:1rem;color:#374151;">Supporting Data</h3>' +
        charts.map((c: any) => {
          if (c.type === 'bar' && c.data?.length > 0) {
            const rows = c.data.map((d: any) =>
              `<tr><td style="padding:4px 12px 4px 0;border-bottom:1px solid #eee;">${d[c.nameKey || 'name']}</td>` +
              `<td style="padding:4px 0;border-bottom:1px solid #eee;text-align:right;font-weight:600;color:${(d[c.dataKey || 'value'] || 0) >= 0 ? '#059669' : '#dc2626'}">${(d[c.dataKey || 'value'] || 0).toFixed(1)}%</td></tr>`
            ).join('');
            return `<div style="margin:16px 0;"><h4 style="font-size:0.85rem;color:#6b7280;margin-bottom:8px;">${c.title}</h4><table style="width:100%;font-size:0.85rem;border-collapse:collapse;">${rows}</table></div>`;
          }
          return '';
        }).join('')
      : '';

    const html = `<!DOCTYPE html><html><head><meta charset="utf-8">
      <meta name="viewport" content="width=device-width, initial-scale=1.0">
      <title>${viewing.title}</title>
      <style>
        * { box-sizing: border-box; }
        body { font-family: Georgia, serif; margin: 0 auto; color: #111; line-height: 1.7; padding: 16px; max-width: 100%; word-wrap: break-word; overflow-wrap: break-word; }
        h1 { font-size: 1.4rem; margin-bottom: 4px; }
        .meta { color: #666; font-size: 0.85rem; margin-bottom: 24px; }
        p { margin-bottom: 1em; }
        h2, h3 { margin-top: 1.5em; margin-bottom: 0.5em; }
        strong { font-weight: 700; }
        img, svg, table, canvas { max-width: 100% !important; width: auto !important; height: auto !important; overflow: hidden; display: block; margin: 8px 0; }
        img[style] { max-width: 100% !important; width: auto !important; }
        .back-btn { display: inline-block; margin-bottom: 16px; padding: 8px 16px; background: #f3f4f6; border: 1px solid #d1d5db; border-radius: 6px; color: #374151; text-decoration: none; font-size: 0.85rem; font-family: sans-serif; cursor: pointer; }
        footer { margin-top: 40px; font-size: 0.75rem; color: #aaa; border-top: 1px solid #eee; padding-top: 12px; }
        @media (min-width: 768px) { body { max-width: 720px; padding: 30px; } }
        @media print { .back-btn { display: none; } body { padding: 10px; max-width: 100%; } }
      </style></head><body>
      <a class="back-btn" href="javascript:void(0)" onclick="window.close(); setTimeout(function(){window.history.back()},100)">Back / Close</a>
      <h1>${viewing.title}</h1>
      <div class="meta">By ${viewing.author_displayName} &middot; ${viewing.published_at ? new Date(viewing.published_at).toLocaleDateString() : ''} &middot; AILabQuant</div>
      ${bodyHtml}
      ${chartHtml}
      <footer>Generated by AILabQuant &mdash; ailabquant.com</footer>
    </body></html>`;

    const win = window.open('', '_blank');
    if (!win) { toast.error('Allow pop-ups to export PDF'); return; }
    win.document.write(html);
    win.document.close();
    win.focus();
    setTimeout(() => win.print(), 400);
  };

  useEffect(() => {
    // Check URL for specific insight ID (from share links)
    const params = new URLSearchParams(window.location.search);
    const sharedId = params.get('id');

    const loadData = async () => {
      try {
        const data = await getPublicInsights(200);
        setInsights(data.insights || []);
      } catch {}

      // Auto-open shared insight
      if (sharedId) {
        try {
          const insight = await getPublicInsight(sharedId);
          setViewing(insight);
        } catch {}
      }

      setLoading(false);
    };
    loadData();
  }, []);

  const handleView = async (id: string) => {
    try {
      const data = await getPublicInsight(id);
      setViewing(data);
    } catch {
      // ignore
    }
  };

  if (viewing) {
    const rawContent = (viewing.content || '')
      .replace(/\\n\\n/g, '\n\n').replace(/\\n/g, '\n');
    const isHtml = viewing.content?.includes('<p>') || viewing.content?.includes('<h');
    const paragraphs = rawContent.split(/\n\n+/).filter(p => p.trim());

    const btnStyle = {
      background: '#fff', border: '1px solid #d1d5db',
      borderRadius: 8, padding: '7px 12px', cursor: 'pointer', color: '#374151',
      fontSize: '0.8rem', display: 'flex' as const, alignItems: 'center' as const, gap: 5
    };

    return (
      <div style={{ maxWidth: 800, margin: '0 auto', padding: '24px 16px', height: '100vh', overflow: 'auto', paddingBottom: 100 }}>
        {/* Toolbar */}
        <div style={{ position: 'sticky', top: 0, background: '#F0F2F5', paddingTop: 8, paddingBottom: 8, zIndex: 10, marginBottom: 20 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <button onClick={() => setViewing(null)} style={btnStyle}>
              Back to Insights
            </button>
            <div style={{ display: 'flex', gap: 6 }}>
              <button onClick={handleCopy} style={btnStyle}>
                {copied ? <Check size={14} /> : <Copy size={14} />}
                {copied ? 'Copied' : 'Copy'}
              </button>
              <button onClick={handlePDF} style={btnStyle}>
                <Download size={14} /> PDF
              </button>
              <button onClick={handleShare} style={btnStyle}>
                <Share2 size={14} /> Share
              </button>
            </div>
          </div>
        </div>

        <h1 style={{ fontSize: '1.5rem', fontWeight: 700, marginBottom: 8, color: '#111827' }}>
          {viewing.title}
        </h1>
        <div style={{ fontSize: '0.8rem', color: '#9ca3af', marginBottom: 20 }}>
          By {viewing.author_displayName} &middot; {viewing.published_at ? new Date(viewing.published_at).toLocaleDateString() : ''}
        </div>

        <div style={{
          lineHeight: 1.8, color: '#374151', fontSize: '0.95rem',
          background: '#fff', borderRadius: 12, padding: 24,
          border: '1px solid #e5e7eb'
        }}>
          {isHtml ? (
            <div dangerouslySetInnerHTML={{ __html: viewing.content || '' }} />
          ) : (
            paragraphs.map((para, i) => {
              const parts = para.split(/(\*\*[^*]+\*\*)/g);
              return (
                <p key={i} style={{ marginBottom: '1em' }}>
                  {parts.map((part, j) => {
                    if (part.startsWith('**') && part.endsWith('**')) {
                      return <strong key={j}>{part.slice(2, -2)}</strong>;
                    }
                    return <span key={j}>{part}</span>;
                  })}
                </p>
              );
            })
          )}
        </div>

        {viewing.charts && viewing.charts.length > 0 && (
          <div style={{ marginTop: 20 }}>
            <h3 style={{ fontSize: '0.9rem', fontWeight: 600, color: '#374151', marginBottom: 12 }}>
              Supporting Data
            </h3>
            {viewing.charts.map((chart, idx) => (
              <InsightChart key={idx} chart={chart} />
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 900, margin: '0 auto', padding: '24px 16px', height: '100vh', overflow: 'auto', paddingBottom: 100 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
        <FileText size={22} />
        <h1 style={{ fontSize: '1.3rem', fontWeight: 700, color: '#111827', margin: 0 }}>Market Insights</h1>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 40, color: '#9ca3af' }}>Loading...</div>
      ) : insights.length === 0 ? (
        <div style={{
          textAlign: 'center', padding: '40px 20px', color: '#9ca3af',
          background: '#fff', borderRadius: 12, border: '1px solid #e5e7eb',
        }}>
          <FileText size={32} style={{ marginBottom: 8, opacity: 0.4 }} />
          <p>No published insights yet.</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {insights.slice(0, visibleCount).map(insight => (
            <div
              key={insight.id}
              onClick={() => handleView(insight.id)}
              style={{
                background: '#fff', borderRadius: 12, border: '1px solid #e5e7eb',
                padding: '20px 24px', cursor: 'pointer',
                boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                transition: 'box-shadow 0.15s',
              }}
            >
              <h2 style={{ fontSize: '1.05rem', fontWeight: 600, color: '#111827', margin: '0 0 6px' }}>
                {insight.title}
              </h2>
              <div style={{ fontSize: '0.8rem', color: '#9ca3af', marginBottom: 8 }}>
                By {insight.author_displayName} &middot; {insight.published_at ? new Date(insight.published_at).toLocaleDateString() : ''} &middot; {insight.format}
              </div>
              {insight.content_preview && (
                <p style={{ fontSize: '0.85rem', color: '#6b7280', margin: 0, lineHeight: 1.5 }}>
                  {insight.content_preview.replace(/<[^>]+>/g, '').replace(/\*\*/g, '').replace(/##/g, '').replace(/\\n/g, ' ').slice(0, 200)}...
                </p>
              )}
            </div>
          ))}
          {insights.length > visibleCount && (
            <div style={{ textAlign: 'center', padding: '16px 0' }}>
              <button
                onClick={() => setVisibleCount(c => c + 12)}
                style={{
                  background: '#fff', border: '1px solid #d1d5db', borderRadius: 8,
                  padding: '10px 24px', cursor: 'pointer', color: '#374151',
                  fontSize: '0.85rem', fontWeight: 500,
                }}
              >
                Show more ({insights.length - visibleCount} remaining)
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
